<template>
  <div class="card">
    <breadcrumbs :items="breadcrumbsItems" />
    <div class="access-denied-content">
      <div class="warning-icon">⚠️</div>
      <h2>{{ s__('AccessDenied|Access Denied') }}</h2>
      <p class="warning-message">
        {{ resolvedMessage }}
      </p>
      <div class="actions">
        <a :href="dashboardUrl" class="btn-link">
          <span class="btn-text">{{ s__('Dashboard|Main') }}</span>
        </a>
        <a v-if="projectTasksUrl" :href="projectTasksUrl" class="btn-link-secondary">
          <span class="btn-text">{{ s__('Tasks|Tasks') }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { s__ } from '@/locale'
import Breadcrumbs from './Breadcrumbs.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'

// Remove loading screen immediately since this page has no data to load
removeInitialLoading()

const props = defineProps({
  message: {
    type: String,
    default: null,
  },
  reason: {
    type: String,
    default: null,
  },
  projectId: {
    type: String,
    default: null,
  },
  projectTitle: {
    type: String,
    default: null,
  },
  chatId: {
    type: String,
    default: null,
  },
  fromId: {
    type: String,
    default: null,
  },
})

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const resolvedMessage = computed(() => {
  if (props.message) return props.message

  if (props.reason === 'not_member') {
    return s__(
      'AccessDenied|You do not have access to this project. Join the related Telegram group chat first, then open the link again.'
    )
  }

  return s__('AccessDenied|This page is available only to project owners and administrators.')
})

const projectTasksUrl = computed(() => {
  if (!props.projectId) return null
  let url = `/projects/${encodeURIComponent(props.projectId)}/dashboard`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const breadcrumbsItems = computed(() => {
  const items = [{ label: s__('Dashboard|Main'), url: dashboardUrl.value }]
  if (props.projectTitle) {
    items.push({ label: props.projectTitle, url: projectTasksUrl.value })
  }
  return items
})
</script>

<style scoped>
.access-denied-content {
  text-align: center;
  padding: 48px 24px;
}

.warning-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

h2 {
  margin: 0 0 16px 0;
  color: #333;
}

.warning-message {
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  line-height: 1.6;
}

.actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

/* Button styles inherit from forms.css */
</style>
